/**
 * DevStatusBarReporter (2026-05-16) — ships the contents of the in-game
 * DevStatusBar to the server every 5s so MCP can query remote sessions
 * without driving a browser.
 *
 * Data sources mirror DevStatusBarContent (src/game/ui/components/
 * DevStatusBar.tsx):
 *   - server: NetworkManager.getRoom() ? 'connected' : 'disconnected'
 *   - services: ServiceRegistry.getServiceNames()
 *   - dead events: EventBus.getInstance().getDeadEvents()
 *   - errors: window 'error' event count since mount + recent log lines
 *     of level WARN/ERROR (from gameLogger.getLogs())
 *   - build SHA: getVersionState() → boot + server SHA + drift
 *
 * Mirrors BrowserPerfReporter's gate + mount conventions. Mounted from
 * main.tsx alphabetically with other Reporters. Pushes via
 * `netobs:devStatusBar` Colyseus message → DevStatusBarBuffer per-session
 * ring → `/api/diag/status-bar` → `dj_h2000_status_bar` MCP tool.
 */

import React, { useEffect } from 'react';
import { EventBus } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import { getVersionState } from '../../hooks/useBuildVersionCheck';
import { isNetobsEnabled } from './isNetobsEnabled';
import type {
  DevStatusBarSnapshotWire,
  DevStatusBarDeadEventCountWire,
} from '../../../shared/diagnostics/DevStatusBarTypes';

const REPORT_INTERVAL_MS = 5000;
const SERVICES_LIST_CAP = 50;
const DEAD_EVENT_BREAKDOWN_CAP = 20;
const RECENT_ERRORS_CAP = 5;
/** gameLogger.LogLevel.WARN — keep in sync with src/utils/logger/LogTypes.ts. */
const LOG_LEVEL_WARN = 4;

function readDeadEventsBreakdown(deadEvents: ReadonlyArray<{ event: string; timestamp: number }>, windowStart: number): {
  breakdown: DevStatusBarDeadEventCountWire[];
  inWindowCount: number;
} {
  const counts = new Map<string, { count: number; lastAt: number }>();
  let inWindowCount = 0;
  for (const d of deadEvents) {
    if (d.timestamp < windowStart) continue;
    inWindowCount++;
    const prev = counts.get(d.event);
    if (prev) {
      prev.count++;
      if (d.timestamp > prev.lastAt) prev.lastAt = d.timestamp;
    } else {
      counts.set(d.event, { count: 1, lastAt: d.timestamp });
    }
  }
  const breakdown: DevStatusBarDeadEventCountWire[] = Array.from(counts.entries())
    .map(([event, v]) => ({ event, count: v.count, lastAt: v.lastAt }))
    .sort((a, b) => b.count - a.count)
    .slice(0, DEAD_EVENT_BREAKDOWN_CAP);
  return { breakdown, inWindowCount };
}

const DevStatusBarReporterInner: React.FC = () => {
  useEffect(() => {
    let errorCount = 0;
    const onWindowError = () => { errorCount++; };
    window.addEventListener('error', onWindowError);

    let lastFlushAt = Date.now();
    const flushTimer = window.setInterval(() => {
      const now = Date.now();
      const windowMs = now - lastFlushAt;
      lastFlushAt = now;
      const windowStart = now - windowMs;

      try {
        const reg = ServiceRegistry.getInstance();
        const eb = EventBus.getInstance();

        // Server connection check — same logic as DevStatusBarContent.
        let server: DevStatusBarSnapshotWire['server'] = 'unknown';
        try {
          const directRoom = (globalThis as any).__helix_room;
          if (directRoom) {
            server = 'connected';
          } else {
            const nm = reg.get('NetworkManager');
            server = nm?.getRoom?.() ? 'connected' : 'disconnected';
          }
        } catch { server = 'unknown'; }

        const serviceNames: string[] = reg.getServiceNames?.() ?? [];
        const servicesList = serviceNames.length > SERVICES_LIST_CAP
          ? serviceNames.slice(0, SERVICES_LIST_CAP)
          : serviceNames;

        const deadEventsRaw = eb.getDeadEvents?.() ?? [];
        const { breakdown, inWindowCount } = readDeadEventsBreakdown(deadEventsRaw, windowStart);

        // Recent errors — gameLogger.getLogs() returns most-recent-last;
        // we filter for WARN+ and reverse so newest is first on the wire.
        const recentErrors: DevStatusBarSnapshotWire['recentErrors'] = [];
        try {
          const logs = gameLogger.getLogs?.() ?? [];
          for (let i = logs.length - 1; i >= 0 && recentErrors.length < RECENT_ERRORS_CAP; i--) {
            const l: any = logs[i];
            const lvl = typeof l.level === 'number' ? l.level : 0;
            if (lvl < LOG_LEVEL_WARN) continue;
            recentErrors.push({
              at: typeof l.timestamp === 'number' ? l.timestamp : now,
              level: lvl,
              category: String(l.category ?? 'unknown').slice(0, 32),
              message: String(l.message ?? '').slice(0, 200),
            });
          }
        } catch { /* logger access failed; ship without recentErrors */ }

        const v = getVersionState();
        const snapshot: DevStatusBarSnapshotWire = {
          capturedAt: now,
          windowMs,
          server,
          servicesCount: serviceNames.length,
          servicesList,
          deadEventsTotal: deadEventsRaw.length,
          deadEventsBreakdown: breakdown,
          deadEventsLastWindow: inWindowCount,
          errorCount,
          recentErrors,
          bootSha: v.bootSha ? v.bootSha.slice(0, 12) : null,
          serverSha: v.lastServerSha ? v.lastServerSha.slice(0, 12) : null,
          buildEnv: v.env ?? null,
          drift: !!v.drift,
        };

        try {
          const room = (globalThis as any).nm?.getRoom?.();
          if (room && typeof room.send === 'function') {
            room.send('netobs:devStatusBar', snapshot);
          }
        } catch {
          // diagnostic-only, never gameplay
        }
      } catch {
        // never let a reporter throw — diagnostic budget only
      }
    }, REPORT_INTERVAL_MS);

    return () => {
      window.clearInterval(flushTimer);
      window.removeEventListener('error', onWindowError);
    };
  }, []);

  return null;
};

export const DevStatusBarReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <DevStatusBarReporterInner />;
};
